// Thin client for the backend API. All calls are same-origin: in dev Vite
// proxies /api to the Express server; in production Express serves both.
//
// Beyond the catalogue CRUD, this client now drives the whole log pipeline:
// CSV ingestion, import-batch management, and the pre-aggregated analytics /
// paginated raw-event queries the dashboard reads.

import { useState, useEffect, useMemo, useCallback } from 'react';

// CSRF synchroniser token. Set from GET /me after login; attached as
// X-CSRF-Token on every state-changing request so the server can reject
// cross-site writes. Held in module state (mirrors the session cookie lifetime).
let csrfToken = null;
export function setCsrfToken(t) { csrfToken = t || null; }

// Admin org selector: when a system_admin picks an org to view, its id is sent as
// X-Org-Id so the server scopes reads/writes to that tenant (null = all orgs).
let activeOrgId = null;
export function setActiveOrgId(id) { activeOrgId = id == null ? null : id; }

function authHeaders(method) {
  const h = {};
  if (method && method !== 'GET') h['Content-Type'] = 'application/json';
  if (method && method !== 'GET' && csrfToken) h['X-CSRF-Token'] = csrfToken;
  if (activeOrgId != null) h['X-Org-Id'] = String(activeOrgId);
  return h;
}

async function request(path, options = {}) {
  const method = options.method || 'GET';
  const res = await fetch('/api' + path, {
    credentials: 'include',
    ...options,
    headers: { ...authHeaders(method), ...(options.headers || {}) },
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try { const body = await res.json(); if (body?.error) message = body.error; } catch { /* ignore */ }
    const err = new Error(message);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

// Build a query string from a filters/options object. Arrays are joined with
// commas; plain objects (colFilters) are JSON-encoded; empty values are dropped.
function qs(params = {}) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue;
    if (Array.isArray(v)) { if (v.length) sp.set(k, v.join(',')); }
    else if (typeof v === 'object') { const s = JSON.stringify(v); if (s && s !== '{}') sp.set(k, s); }
    else sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? '?' + s : '';
}

export const api = {
  // ── Catalogue ──────────────────────────────────────────────────────────────
  getCatalogue: () => request('/catalogue'),
  getProducts: () => request('/products'),
  createProduct: (data) => request('/products', { method: 'POST', body: JSON.stringify(data) }),
  updateProduct: (id, data) => request(`/products/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteProduct: (id) => request(`/products/${id}`, { method: 'DELETE' }),
  addDomain: (id, domain) => request(`/products/${id}/domains`, { method: 'POST', body: JSON.stringify({ domain }) }),
  removeDomain: (id, domainId) => request(`/products/${id}/domains/${domainId}`, { method: 'DELETE' }),
  getNoise: () => request('/noise'),
  addNoise: (value, kind) => request('/noise', { method: 'POST', body: JSON.stringify({ value, kind }) }),
  removeNoise: (id) => request(`/noise/${id}`, { method: 'DELETE' }),

  // ── Ingestion ──────────────────────────────────────────────────────────────
  // Upload a single CSV file as the raw request body. The browser sets the
  // Content-Type from the File, so the server streams it through untouched.
  uploadCSV: async (file, audience) => {
    const path = `/api/ingest${qs({ audience, filename: file.name })}`;
    const headers = {};
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    if (activeOrgId != null) headers['X-Org-Id'] = String(activeOrgId);
    const res = await fetch(path, { method: 'POST', body: file, credentials: 'include', headers });
    if (!res.ok) {
      let message = `Upload failed (${res.status})`;
      try { const body = await res.json(); if (body?.error) message = body.error; } catch { /* ignore */ }
      throw new Error(message);
    }
    return res.json();
  },
  ingestSample: () => request('/ingest/sample', { method: 'POST' }),

  // ── Batches ────────────────────────────────────────────────────────────────
  getBatches: () => request('/batches'),
  getBatch: (id) => request(`/batches/${id}`),
  deleteBatch: (id) => request(`/batches/${id}`, { method: 'DELETE' }),
  // Remove only the built-in sample data; real uploaded CSVs are untouched.
  clearSampleData: () => request('/batches/sample', { method: 'DELETE' }),

  // ── Re-scan (re-detect stored events against the current catalogue) ──────────
  startRescan: () => request('/rescan', { method: 'POST' }),
  getRescan: () => request('/rescan'),

  // ── Analytics (read from rollups) ────────────────────────────────────────────
  getMeta: () => request('/analytics/meta'),
  getOverview: (filters) => request('/analytics/overview' + qs(filters)),
  getBySchool: (filters) => request('/analytics/by-school' + qs(filters)),
  getByProduct: (filters) => request('/analytics/by-product' + qs(filters)),
  getSpendVsUsage: (filters) => request('/analytics/spend-vs-usage' + qs(filters)),
  getEvents: (params) => request('/events' + qs(params)),
  // Direct URL for the streamed CSV export (used as an <a href> download).
  eventsCsvUrl: (params) => '/api/events.csv' + qs(params),

  // ── Schools & Academies registry ──────────────────────────────────────────────
  getSchools: () => request('/schools'),
  createSchool: (data) => request('/schools', { method: 'POST', body: JSON.stringify(data) }),
  updateSchool: (id, data) => request(`/schools/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSchool: (id) => request(`/schools/${id}`, { method: 'DELETE' }),
  addSchoolDomain: (id, domain) => request(`/schools/${id}/domains`, { method: 'POST', body: JSON.stringify({ domain }) }),
  removeSchoolDomain: (id, domainId) => request(`/schools/${id}/domains/${domainId}`, { method: 'DELETE' }),

  // ── School groups ─────────────────────────────────────────────────────────────
  getGroups: () => request('/school-groups'),
  createGroup: (data) => request('/school-groups', { method: 'POST', body: JSON.stringify(data) }),
  updateGroup: (id, data) => request(`/school-groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGroup: (id) => request(`/school-groups/${id}`, { method: 'DELETE' }),
  addGroupMember: (id, schoolId) => request(`/school-groups/${id}/members`, { method: 'POST', body: JSON.stringify({ schoolId }) }),
  removeGroupMember: (id, schoolId) => request(`/school-groups/${id}/members/${schoolId}`, { method: 'DELETE' }),

  // ── Contracts ─────────────────────────────────────────────────────────────────
  getContracts: () => request('/contracts'),
  getRenewals: () => request('/contracts/renewals'),
  getContract: (id) => request(`/contracts/${id}`),
  createContract: (data) => request('/contracts', { method: 'POST', body: JSON.stringify(data) }),
  updateContract: (id, data) => request(`/contracts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteContract: (id) => request(`/contracts/${id}`, { method: 'DELETE' }),

  // AI research
  getResearch: (id) => request(`/products/${id}/research`),
  startResearch: (id) => request(`/products/${id}/research`, { method: 'POST' }),
  applyResearchPrice: (id) => request(`/products/${id}/research/apply-price`, { method: 'POST' }),

  // ── Auth / session ──────────────────────────────────────────────────────────
  me: () => request('/me'),
  login: (email, password) => request('/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => request('/logout', { method: 'POST' }),

  // ── Organisations (system_admin) ──────────────────────────────────────────────
  getOrgs: () => request('/orgs'),
  // Public: fetch an org's logo (no session needed) for branded login screens.
  getOrgLogo: (id) => request(`/orgs/${id}/logo`),
  createOrg: (name, researchCredits, logo) => request('/orgs', { method: 'POST', body: JSON.stringify({ name, researchCredits, ...(logo !== undefined ? { logo } : {}) }) }),
  updateOrg: (id, name, researchCredits, logo) => request(`/orgs/${id}`, { method: 'PUT', body: JSON.stringify({ name, researchCredits, ...(logo !== undefined ? { logo } : {}) }) }),
  deleteOrg: (id) => request(`/orgs/${id}`, { method: 'DELETE' }),

  // ── Users (system_admin: any; org_admin: own org) ─────────────────────────────
  getUsers: (orgId) => request('/users' + qs({ orgId })),
  createUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  setUserPassword: (id, password) => request(`/users/${id}/password`, { method: 'POST', body: JSON.stringify({ password }) }),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),
};

// Small data-fetching hook shared by the views. Re-runs whenever `deps` change;
// ignores results from superseded requests so fast filter changes don't race.
export function useApi(fn, deps, enabled = true) {
  const [state, setState] = useState({ loading: enabled, error: null, data: null });
  useEffect(() => {
    if (!enabled) { setState({ loading: false, error: null, data: null }); return; }
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fn()
      .then((data) => { if (!cancelled) setState({ loading: false, error: null, data }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, error: e.message, data: null }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

// Filter-driven dashboard fetch shared by the analytics views. Re-runs whenever
// the stringified `keyValue` (usually the active filters) changes, and exposes a
// `retry()` that forces a refetch by bumping an internal nonce. Encapsulates the
// nonce + JSON-key + useApi boilerplate the views each hand-rolled.
export function useFilteredApi(fetcher, keyValue, enabled = true) {
  const [nonce, setNonce] = useState(0);
  const dep = useMemo(() => JSON.stringify(keyValue) + '|' + nonce, [keyValue, nonce]);
  const { loading, error, data } = useApi(fetcher, [dep], enabled);
  const retry = useCallback(() => setNonce((n) => n + 1), []);
  return { loading, error, data, retry };
}
